import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, MessageCircle, Music, Video } from "lucide-react";
import { bookColors, poems, songs, whatsappBookLink } from "../data/content";
import NewsletterSignup from "../components/site/NewsletterSignup";

const PoemPage = () => {
    const { id } = useParams();
    const poem = poems.find((p) => p.id === id);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [id]);

    if (!poem) {
        return (
            <main data-testid="poem-page" className="min-h-screen bg-paper text-ink flex items-center justify-center">
                <div className="text-center px-5">
                    <p className="font-display italic text-3xl text-ink/70">
                        Este poema no está aquí…
                    </p>
                    <Link
                        to="/"
                        data-testid="poem-page-back"
                        className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.22em] text-crimson"
                    >
                        ← Volver al inicio
                    </Link>
                </div>
            </main>
        );
    }

    const accent = bookColors[poem.bookId] || "#A63D2E";
    const song = songs.find((s) => s.poemId === poem.id);

    return (
        <main data-testid="poem-page" className="min-h-screen bg-paper text-ink">
            <div className="max-w-2xl mx-auto px-5 py-14 sm:py-20">
                <Link
                    to="/"
                    data-testid="poem-page-back"
                    className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-subtle hover:text-ink transition-colors"
                >
                    <ArrowLeft size={13} />
                    José Luis Garcés Maldonado
                </Link>
                <p
                    data-testid="poem-page-kicker"
                    className="mt-12 font-mono text-[10px] uppercase tracking-[0.3em]"
                    style={{ color: accent }}
                >
                    {poem.book} · {poem.year}
                </p>
                <h1
                    data-testid="poem-page-title"
                    className="mt-4 font-display text-4xl sm:text-6xl font-light tracking-tight text-ink"
                >
                    {poem.title}
                </h1>
                <div className="mt-10 sm:mt-14 font-display text-xl sm:text-2xl leading-relaxed text-ink/90">
                    {poem.lines.map((line, i) =>
                        line === "" ? <div key={i} className="h-5" /> : <p key={i}>{line}</p>
                    )}
                </div>
                <p className="mt-14 font-mono text-[10px] uppercase tracking-[0.22em] text-subtle">
                    José Luis Garcés Maldonado
                </p>
                {(poem.videoId || song) && (
                    <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-3">
                        {song && (
                            <Link
                                data-testid="poem-page-song-link"
                                to={`/?escuchar=${song.id}`}
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-golddeep hover:text-ink transition-colors"
                            >
                                <Music size={13} />
                                Escuchar la canción
                            </Link>
                        )}
                        {poem.videoId && (
                            <Link
                                data-testid="poem-page-video-link"
                                to={`/?ver-video=${poem.videoId}`}
                                className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-crimson hover:text-ink transition-colors"
                            >
                                <Video size={13} />
                                Ver el vídeo poema
                            </Link>
                        )}
                    </div>
                )}
                <div className="mt-16 border-t border-line pt-12 text-center">
                    <p className="font-display italic text-xl text-ink/70">
                        ¿Quieres este poemario en tus manos?
                    </p>
                    <a
                        data-testid="poem-page-buy-button"
                        href={whatsappBookLink(poem.book)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-6 inline-flex items-center gap-2.5 bg-crimson text-paper px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] hover:bg-ink transition-colors duration-300"
                    >
                        <MessageCircle size={14} />
                        Comprar «{poem.book}» por WhatsApp
                    </a>
                </div>
            </div>
            <footer className="border-t border-line bg-cream">
                <NewsletterSignup />
            </footer>
        </main>
    );
};

export default PoemPage;
